import { ScrollableRegion } from '@syncanix/ui';

import { INLINE_LINK } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import {
  commentKey,
  QUESTIONNAIRE,
  questionKey,
  type QuestionnaireAnswer,
} from '@/features/trust/content/questionnaire';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { ReactElement } from 'react';

const ANSWER_CHIP_CLASS: Record<QuestionnaireAnswer, string> = {
  yes: 'bg-success/15 text-success-foreground',
  partial: 'bg-warning/15 text-warning-foreground',
  planned: 'bg-[color:var(--bd-sunk)] text-[color:var(--bd-muted)]',
};

/**
 * Pre-completed security questionnaire (`/trust/security-questionnaire`) —
 * launch-readiness B5's "pre-completed CAIQ-Lite + VSA-Core".
 *
 * One row per CAIQ-Lite domain (mapped to its VSA-Core area) with an honest
 * three-state answer: yes / partial / planned. The meaning is always carried
 * in the text label, never by chip colour alone (WCAG 1.4.1). Print-friendly
 * by construction (semantic table, no interactivity) so a reviewer can save
 * it to PDF and attach it to their assessment; the page invites requests for
 * framework-native sheets by email.
 */
export function Questionnaire(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('questionnaire.meta.title'),
    description: t('questionnaire.meta.description'),
    path: '/trust/security-questionnaire',
  });
  return (
    <LegalPage
      breadcrumb={t('questionnaire.breadcrumb')}
      title={t('questionnaire.heading')}
      intro={t('questionnaire.intro')}
      width="default"
    >
      <ScrollableRegion aria-label={t('questionnaire.table.caption')} className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{t('questionnaire.table.caption')}</caption>
          <thead>
            <tr className="border-b border-[color:var(--bd-line-3)]">
              <th scope="col" className="py-2 pe-3 text-start font-semibold">
                {t('questionnaire.col.domain')}
              </th>
              <th scope="col" className="py-2 pe-3 text-start font-semibold">
                {t('questionnaire.col.question')}
              </th>
              <th scope="col" className="py-2 pe-3 text-start font-semibold">
                {t('questionnaire.col.answer')}
              </th>
              <th scope="col" className="py-2 text-start font-semibold">
                {t('questionnaire.col.detail')}
              </th>
            </tr>
          </thead>
          <tbody>
            {QUESTIONNAIRE.rows.map((row) => (
              <tr
                key={row.id}
                data-q={row.id}
                className="border-b border-[color:var(--bd-line)] align-top"
              >
                <th scope="row" className="py-3 pe-3 text-start font-medium">
                  <span className="block">{row.caiq}</span>
                  <span className="block text-xs font-normal text-[color:var(--bd-muted)]">
                    {row.vsa}
                  </span>
                </th>
                <td className="py-3 pe-3 text-[color:var(--bd-muted)]">{t(questionKey(row))}</td>
                <td className="py-3 pe-3">
                  <span
                    data-answer={row.answer}
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${ANSWER_CHIP_CLASS[row.answer]}`}
                  >
                    {t(`questionnaire.answer.${row.answer}`)}
                  </span>
                </td>
                <td className="py-3 text-[color:var(--bd-muted)]">{t(commentKey(row))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollableRegion>

      <p className="mt-6 text-sm text-[color:var(--bd-muted)]">
        {t('questionnaire.fullSheets')}{' '}
        <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
          admin@syncanix.com
        </a>
        .
      </p>
      <p className="mt-2 text-sm text-[color:var(--bd-muted)]">
        {t('questionnaire.reviewed', { date: QUESTIONNAIRE.lastReviewed })}
      </p>
    </LegalPage>
  );
}
